import React, { useEffect, useRef, useState } from "react";
import {
  ArrowRight,
  Building2,
  CheckCircle2,
  Leaf,
  Loader2,
  Mail,
  User,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { submitEmployeeReferral } from "@/services/marketingService";
import { ROUTE_PATHS } from "@/routes/routePaths";

const REDIRECT_DELAY_MS = 3500;

interface CorporateCarpoolDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const CorporateCarpoolDialog: React.FC<CorporateCarpoolDialogProps> = ({
  open,
  onOpenChange,
}) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [hrEmail, setHrEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const redirectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!open) {
      setName("");
      setEmail("");
      setHrEmail("");
      setSubmitting(false);
      setError(null);
      setSubmitted(false);
      if (redirectTimer.current) {
        clearTimeout(redirectTimer.current);
        redirectTimer.current = null;
      }
    }
  }, [open]);

  useEffect(() => {
    return () => {
      if (redirectTimer.current) clearTimeout(redirectTimer.current);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await submitEmployeeReferral({
        userName: name.trim(),
        userEmail: email.trim(),
        hrEmail: hrEmail.trim() || undefined,
      });
      setSubmitted(true);
      redirectTimer.current = setTimeout(() => {
        window.location.href = ROUTE_PATHS.CORPORATE_CARPOOL;
      }, REDIRECT_DELAY_MS);
    } catch {
      setError(
        "Something went wrong while submitting. Please try again in a moment."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md overflow-hidden p-0">
        <div className="[perspective:1600px]">
          <div
            className={cn(
              "relative transition-transform duration-700 [transform-style:preserve-3d]",
              submitted && "[transform:rotateY(180deg)]"
            )}
          >
            {/* Front: program info + referral form */}
            <div className="[backface-visibility:hidden]">
              <div className="bg-host-gradient px-6 pb-5 pt-6 text-primary-foreground">
                <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-primary-foreground/15 px-3 py-1 text-xs font-semibold backdrop-blur">
                  <Leaf className="h-3.5 w-3.5" />
                  Duri for Organizations
                </div>
                <DialogHeader>
                  <DialogTitle className="text-xl font-bold text-primary-foreground">
                    Free Corporate Carpool Program
                  </DialogTitle>
                  <DialogDescription className="text-sm leading-relaxed text-primary-foreground/90">
                    Help your colleagues commute smarter by sharing daily rides
                    with verified co-workers. Reduce travel costs, ease traffic
                    congestion, and lower your company&apos;s carbon footprint—all
                    at absolutely no cost to your organization. Share this with
                    your HR team and help build a greener, more connected
                    workplace.
                  </DialogDescription>
                </DialogHeader>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4 px-6 pb-6 pt-5">
                <div className="space-y-1.5">
                  <Label htmlFor="cc-name">Your Name *</Label>
                  <div className="relative">
                    <User className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="cc-name"
                      required
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="e.g. Priya Sharma"
                      className="pl-9"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="cc-email">Your Email *</Label>
                  <div className="relative">
                    <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="cc-email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@company.com"
                      className="pl-9"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="cc-hr-email" className="flex items-center gap-1">
                    HR Email
                    <span className="text-xs font-normal text-muted-foreground">
                      (optional)
                    </span>
                  </Label>
                  <div className="relative">
                    <Building2 className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="cc-hr-email"
                      type="email"
                      value={hrEmail}
                      onChange={(e) => setHrEmail(e.target.value)}
                      placeholder="hr@company.com"
                      className="pl-9"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    We&apos;ll gently loop in your HR team to set up the program.
                  </p>
                </div>

                {error && (
                  <p className="text-sm font-medium text-destructive">{error}</p>
                )}

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-gradient-to-r from-[#22C55E] to-[#16A34A] text-white hover:from-[#16A34A] hover:to-[#15803D]"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                      Submitting…
                    </>
                  ) : (
                    <>
                      Refer My Company
                      <ArrowRight className="ml-1.5 h-4 w-4" />
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* Back: thank-you + redirect */}
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-host-gradient px-6 py-10 text-center text-primary-foreground [backface-visibility:hidden] [transform:rotateY(180deg)]">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-foreground/15 backdrop-blur">
                <CheckCircle2 className="h-9 w-9" />
              </div>
              <h3 className="text-xl font-bold">
                Thank You for Being Part of Green Mobility
              </h3>
              <p className="max-w-sm text-sm text-primary-foreground/90">
                Your referral means cleaner air, lighter traffic and happier
                commutes. We&apos;re taking you to the Corporate Carpool Program
                now…
              </p>
              <div className="flex items-center gap-2 text-sm text-primary-foreground/80">
                <Loader2 className="h-4 w-4 animate-spin" />
                Redirecting…
              </div>
              <a
                href={ROUTE_PATHS.CORPORATE_CARPOOL}
                className="text-xs font-semibold text-primary-foreground underline underline-offset-4 hover:text-primary-foreground/90"
              >
                Go now
              </a>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
